import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { Plus, Trash2, ChevronUp, ChevronDown, ToggleLeft, ToggleRight, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reuses the existing image upload pattern from AdminApp.jsx
function ImageUpload({ value, onChange, auth }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/upload`, fd, auth({ headers: { "Content-Type": "multipart/form-data" } }));
      onChange(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
    } finally { setUploading(false); }
  };
  return (
    <div className="space-y-2">
      {value && <img src={value} alt="" className="h-32 w-full md:w-72 object-cover rounded border border-[#E5DDD3]" />}
      <input className="admin-field" placeholder="Image URL or upload below" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid="sp-hero-image-url" />
      <input type="file" accept="image/*" onChange={(e) => upload(e.target.files?.[0])} className="text-sm text-[#897F71]" data-testid="sp-hero-image-file" />
      {uploading && <p className="admin-helper">Uploading…</p>}
    </div>
  );
}

function emptyItem() {
  return { id: `new-${Math.random().toString(36).slice(2, 9)}`, name: "", price: "", duration: "", description: "", order: 0, active: true };
}

export default function ServicePricingAdmin() {
  const { auth } = useAuth();
  const [groups, setGroups] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [status, setStatus] = useState("");
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/service-pricing`, auth());
      setGroups(data);
    } catch (e) {
      setStatus("Could not load service pricing.");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  if (!groups.length) {
    return <div className="admin-card p-6"><p className="admin-helper">Loading service pricing…</p></div>;
  }

  const group = groups[activeIdx];

  const updGroup = (patch) => {
    const next = [...groups];
    next[activeIdx] = { ...group, ...patch };
    setGroups(next);
  };

  const updItem = (itemIdx, patch) => {
    const items = [...(group.items || [])];
    items[itemIdx] = { ...items[itemIdx], ...patch };
    updGroup({ items });
  };

  const addItem = () => {
    const items = [...(group.items || []), { ...emptyItem(), order: (group.items || []).length }];
    updGroup({ items });
  };

  const removeItem = (itemIdx) => {
    const items = (group.items || []).filter((_, i) => i !== itemIdx).map((it, i) => ({ ...it, order: i }));
    updGroup({ items });
  };

  const moveItem = (itemIdx, dir) => {
    const items = [...(group.items || [])];
    const target = itemIdx + dir;
    if (target < 0 || target >= items.length) return;
    [items[itemIdx], items[target]] = [items[target], items[itemIdx]];
    items.forEach((it, i) => { it.order = i; });
    updGroup({ items });
  };

  const save = async () => {
    setSavingId(group.id);
    setStatus("");
    try {
      // Normalise order
      const items = (group.items || []).map((it, i) => ({ ...it, order: i }));
      const payload = { ...group, items };
      await axios.put(`${API}/admin/service-pricing/${group.id}`, payload, auth());
      setStatus(`Saved ${group.group_title}.`);
      await load();
    } catch (e) {
      setStatus("Save failed — please try again.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div data-testid="service-pricing-admin">
      {/* Header + group tabs */}
      <div className="admin-card p-6 md:p-8 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="admin-h2">Service Pricing / Service Pop-Outs</h2>
            <p className="admin-helper mt-1">Edit the pricing pop-outs that appear when visitors click a service card. Changes go live instantly.</p>
          </div>
          <button className="admin-btn" onClick={save} disabled={savingId === group.id} data-testid="sp-save-btn">
            <Save className="h-4 w-4" /> {savingId === group.id ? "Saving…" : "Save changes"}
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {groups.map((g, i) => (
            <button
              key={g.id}
              className={`admin-tab ${i === activeIdx ? "active" : ""}`}
              onClick={() => setActiveIdx(i)}
              data-testid={`sp-tab-${g.id}`}
            >
              {g.group_title}
            </button>
          ))}
        </div>

        {status && <p className="admin-helper mt-4">{status}</p>}
      </div>

      {/* Group fields */}
      <div className="admin-card p-6 md:p-8 mb-6">
        <h3 className="admin-h2 mb-4">Group details</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="admin-label">Group title</label>
            <input className="admin-field" value={group.group_title || ""} onChange={(e) => updGroup({ group_title: e.target.value })} data-testid="sp-group-title" />
          </div>
          <div>
            <label className="admin-label">Eyebrow label</label>
            <input className="admin-field" value={group.eyebrow || ""} onChange={(e) => updGroup({ eyebrow: e.target.value })} placeholder="e.g. Service Details" data-testid="sp-group-eyebrow" />
          </div>
          <div className="md:col-span-2">
            <label className="admin-label">Hero image</label>
            <ImageUpload value={group.hero_image} onChange={(v) => updGroup({ hero_image: v })} auth={auth} />
          </div>
          <div className="md:col-span-2">
            <label className="admin-label">Footer note</label>
            <textarea className="admin-field min-h-[90px] resize-y" value={group.footer_note || ""} onChange={(e) => updGroup({ footer_note: e.target.value })} data-testid="sp-group-footer" />
          </div>
          <div>
            <label className="admin-label">CTA text</label>
            <input className="admin-field" value={group.cta_text || ""} onChange={(e) => updGroup({ cta_text: e.target.value })} placeholder="Enquire now" data-testid="sp-group-cta-text" />
          </div>
          <div>
            <label className="admin-label">CTA link</label>
            <input className="admin-field" value={group.cta_link || ""} onChange={(e) => updGroup({ cta_link: e.target.value })} placeholder="/contact#enquiry-form" data-testid="sp-group-cta-link" />
          </div>
        </div>
      </div>

      {/* Items list */}
      <div className="admin-card p-6 md:p-8 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="admin-h2">Service items</h3>
          <button className="admin-btn ghost" onClick={addItem} data-testid="sp-add-item-btn">
            <Plus className="h-4 w-4" /> Add item
          </button>
        </div>

        {(group.items || []).length === 0 && (
          <p className="admin-helper">No items yet. Click <strong>Add item</strong> to create one.</p>
        )}

        <div className="space-y-3">
          {(group.items || []).map((it, i) => (
            <div key={it.id} className={`border ${it.active === false ? "border-dashed border-[#D7CFC3] bg-[#F9F5EF]" : "border-[#E5DDD3] bg-white"} rounded-lg p-4`} data-testid={`sp-item-${i}`}>
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <span className="admin-eyebrow">Item #{i + 1}{it.active === false ? " · INACTIVE" : ""}</span>
                <div className="flex items-center gap-2">
                  <button className="admin-btn ghost" title="Move up" onClick={() => moveItem(i, -1)} disabled={i === 0} data-testid={`sp-item-up-${i}`}>
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button className="admin-btn ghost" title="Move down" onClick={() => moveItem(i, 1)} disabled={i === group.items.length - 1} data-testid={`sp-item-down-${i}`}>
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <button
                    className="admin-btn ghost"
                    onClick={() => updItem(i, { active: !(it.active !== false) })}
                    title={it.active !== false ? "Mark inactive" : "Mark active"}
                    data-testid={`sp-item-toggle-${i}`}
                  >
                    {it.active !== false ? <ToggleRight className="h-4 w-4 text-coral" /> : <ToggleLeft className="h-4 w-4" />}
                    {it.active !== false ? "Active" : "Inactive"}
                  </button>
                  <button className="admin-btn danger" onClick={() => removeItem(i)} data-testid={`sp-item-delete-${i}`}>
                    <Trash2 className="h-4 w-4" /> Delete
                  </button>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-12">
                <div className="md:col-span-6">
                  <label className="admin-label">Service name</label>
                  <input className="admin-field" value={it.name || ""} onChange={(e) => updItem(i, { name: e.target.value })} data-testid={`sp-item-name-${i}`} />
                </div>
                <div className="md:col-span-3">
                  <label className="admin-label">Price</label>
                  <input className="admin-field" value={it.price || ""} onChange={(e) => updItem(i, { price: e.target.value })} placeholder="$200 or from $130" data-testid={`sp-item-price-${i}`} />
                </div>
                <div className="md:col-span-3">
                  <label className="admin-label">Duration / Time</label>
                  <input className="admin-field" value={it.duration || ""} onChange={(e) => updItem(i, { duration: e.target.value })} placeholder="2 hours" data-testid={`sp-item-duration-${i}`} />
                </div>
                <div className="md:col-span-12">
                  <label className="admin-label">Description / Note</label>
                  <textarea className="admin-field min-h-[70px] resize-y" value={it.description || ""} onChange={(e) => updItem(i, { description: e.target.value })} data-testid={`sp-item-description-${i}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="admin-card p-4 flex items-center justify-end gap-3">
        <span className="admin-helper">Changes only take effect after Save.</span>
        <button className="admin-btn" onClick={save} disabled={savingId === group.id} data-testid="sp-save-btn-bottom">
          <Save className="h-4 w-4" /> {savingId === group.id ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
